<script setup>
import { computed } from 'vue';
import { formatEgyptTime } from '@/composables/examError';

// Presentation-only, single source of the post-exam result UI — reused by the
// exam page (immediately after submit / on reopening a completed exam) and the
// review page (my_exams/show.vue), so the score card is never duplicated.
// All numbers come from the backend (existing scoring/attempt rules) — nothing
// here recalculates a score, a percentage, or a pass/fail threshold.
const props = defineProps({
  result: { type: Object, required: true },
  // Human labels (optional — purely cosmetic).
  examTitle: { type: String, default: '' },
  lessonTitle: { type: String, default: '' },
  // null = unlimited attempts (homework); 0 = none left; >0 = attempts left.
  remainingAttempts: { type: Number, default: null },
  answersAvailable: { type: Boolean, default: true },
  answersReleaseAt: { type: String, default: null },
  // Action visibility/targets — the component never assumes a route.
  showReviewButton: { type: Boolean, default: true },
  reviewTo: { type: String, default: null },
  homeTo: { type: String, default: '/exams' },
  homeLabel: { type: String, default: 'العودة إلى الامتحانات' },
  // retakeTo: navigate via router-link. showRetake (without retakeTo): emit
  // 'retake' instead, for pages that need to reset local state first.
  retakeTo: { type: String, default: null },
  showRetake: { type: Boolean, default: false },
  retakeLabel: { type: String, default: 'محاولة جديدة' },
});

const emit = defineEmits(['retake']);

const passed = computed(() => !!props.result?.passed);

const percentage = computed(() => {
  const p = props.result?.percentage;
  return typeof p === 'number' ? p : Number(p ?? 0);
});

const formattedPercentage = computed(() => {
  // Show up to 2 decimals, but drop a trailing ".00" for whole numbers.
  const p = percentage.value;
  return Number.isInteger(p) ? String(p) : p.toFixed(2).replace(/\.?0+$/, '');
});

const remainingLabel = computed(() =>
  props.remainingAttempts === null ? 'غير محدود' : String(props.remainingAttempts)
);

const submitReasonLabel = computed(() => {
  if (props.result?.submit_reason === 'timeout') return 'تسليم تلقائي (انتهى الوقت)';
  if (props.result?.submit_reason === 'manual') return 'تسليم يدوي';
  return null;
});

const releaseAtText = computed(() =>
  props.answersReleaseAt ? formatEgyptTime(props.answersReleaseAt) : ''
);
</script>

<template>
  <div class="ers" :class="result.results_available === false ? 'ers--pending' : passed ? 'ers--pass' : 'ers--fail'">
    <div class="ers-card">
      <div class="ers-glow"></div>

      <!-- Header -->
      <div class="ers-header">
        <span class="ers-eyebrow">نتيجة الامتحان</span>
        <h1 v-if="examTitle" class="ers-title">{{ examTitle }}</h1>
        <p v-if="lessonTitle" class="ers-subtitle">{{ lessonTitle }}</p>
      </div>

      <!-- Big score ring -->
      <div class="ers-ring-wrap" v-if="result.results_available !== false">
        <div class="ers-ring">
          <div class="ers-ring-inner">
            <span class="ers-pct">{{ formattedPercentage }}%</span>
            <span class="ers-verdict">{{ passed ? 'ناجح' : 'غير ناجح' }}</span>
          </div>
        </div>
      </div>

      <!-- Status banner -->
      <div v-if="result.results_available === false" class="ers-banner">تم حفظ تسليم الامتحان. النتيجة غير متاحة للعرض حالياً.</div>
      <div v-else class="ers-banner">
        {{ passed ? '🎉 مبروك! بالتوفيق' : '💪 لا تستسلم، راجع إجاباتك وحاول مرة أخرى' }}
      </div>

      <!-- Stat cards -->
      <div class="ers-stats">
        <div class="ers-stat">
          <span class="ers-stat-icon">❓</span>
          <span class="ers-stat-value">{{ result.total_questions ?? '—' }}</span>
          <span class="ers-stat-label">عدد الأسئلة</span>
        </div>
        <div class="ers-stat" v-if="result.results_available !== false">
          <span class="ers-stat-icon">🏆</span>
          <span class="ers-stat-value">{{ result.student_score }} <small>/ {{ result.max_score }}</small></span>
          <span class="ers-stat-label">الدرجة المستحقة</span>
        </div>
        <div class="ers-stat" v-if="result.results_available !== false">
          <span class="ers-stat-icon">✅</span>
          <span class="ers-stat-value">{{ result.correct_answers ?? '—' }}</span>
          <span class="ers-stat-label">إجابات صحيحة</span>
        </div>
        <div class="ers-stat">
          <span class="ers-stat-icon">🔁</span>
          <span class="ers-stat-value">{{ remainingLabel }}</span>
          <span class="ers-stat-label">المحاولات المتبقية</span>
        </div>
      </div>

      <!-- Submission meta -->
      <div v-if="submitReasonLabel || result.submitted_at" class="ers-meta">
        <span v-if="submitReasonLabel">{{ submitReasonLabel }}</span>
        <span v-if="submitReasonLabel && result.submitted_at" class="ers-meta-dot">•</span>
        <span v-if="result.submitted_at">تم التسليم: {{ formatEgyptTime(result.submitted_at) }}</span>
      </div>

      <!-- Answers availability -->
      <div v-if="!answersAvailable" class="ers-release-note">
        {{ releaseAtText ? '📅 الإجابات والنتيجة ستكون متاحة ابتداءً من ' + releaseAtText : 'الإجابات والنتيجة غير متاحة للعرض للطالب.' }}
      </div>

      <!-- Actions -->
      <div class="ers-actions">
        <router-link
          v-if="answersAvailable && showReviewButton && reviewTo"
          :to="reviewTo"
          class="ers-btn ers-btn--primary"
        >
          📋 مراجعة الإجابات
        </router-link>
        <router-link
          v-if="retakeTo"
          :to="retakeTo"
          class="ers-btn ers-btn--accent"
        >
          🔄 {{ retakeLabel }}
        </router-link>
        <button
          v-else-if="showRetake"
          type="button"
          class="ers-btn ers-btn--accent"
          @click="emit('retake')"
        >
          🔄 {{ retakeLabel }}
        </button>
        <router-link :to="homeTo" class="ers-btn ers-btn--muted">
          {{ homeLabel }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; }

.ers {
  direction: rtl;
  font-family: 'Cairo', sans-serif;
  width: 100%;
}

.ers-card {
  position: relative;
  overflow: hidden;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .1);
  padding: 2.25rem 2rem;
  text-align: center;
}

.ers-glow {
  position: absolute; top: -70px; left: 50%; transform: translateX(-50%);
  width: 320px; height: 320px; border-radius: 50%; pointer-events: none; opacity: .28;
}
.ers--pass .ers-glow { background: radial-gradient(circle, #22C55E, transparent); }
.ers--fail .ers-glow { background: radial-gradient(circle, #EF4444, transparent); }

.ers-header { position: relative; z-index: 1; margin-bottom: 1.5rem; }
.ers-eyebrow {
  display: inline-block; font-size: .8rem; font-weight: 800; letter-spacing: .02em;
  color: #5B5FEF; background: rgba(91, 95, 239, .08);
  padding: .3rem 1rem; border-radius: 99px; margin-bottom: .75rem;
}
.ers-title { font-size: 1.5rem; font-weight: 800; color: #1A1A1A; margin: 0 0 .25rem; line-height: 1.4; }
.ers-subtitle { font-size: .9rem; color: #6B7280; margin: 0; }

.ers-ring-wrap { position: relative; z-index: 1; display: flex; justify-content: center; margin-bottom: 1.5rem; }
.ers-ring {
  width: 160px; height: 160px; border-radius: 50%; padding: 6px;
  display: flex; align-items: center; justify-content: center;
}
.ers--pass .ers-ring { background: linear-gradient(135deg, #22C55E, #16a34a); box-shadow: 0 20px 50px rgba(34, 197, 94, .35); }
.ers--fail .ers-ring { background: linear-gradient(135deg, #EF4444, #dc2626); box-shadow: 0 20px 50px rgba(239, 68, 68, .35); }
.ers-ring-inner {
  background: #fff; border-radius: 50%; width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .3rem;
}
.ers-pct { font-size: 2.1rem; font-weight: 800; color: #1A1A1A; }
.ers-verdict { font-size: .95rem; font-weight: 800; }
.ers--pass .ers-verdict { color: #16a34a; }
.ers--fail .ers-verdict { color: #dc2626; }

.ers-banner {
  position: relative; z-index: 1;
  display: inline-block; font-weight: 700; font-size: .95rem;
  padding: .75rem 1.5rem; border-radius: 14px; margin-bottom: 1.75rem;
}
.ers--pass .ers-banner { background: rgba(34, 197, 94, .08); color: #16a34a; border: 1.5px solid rgba(34, 197, 94, .2); }
.ers--fail .ers-banner { background: rgba(239, 68, 68, .06); color: #dc2626; border: 1.5px solid rgba(239, 68, 68, .15); }

.ers-stats {
  position: relative; z-index: 1;
  display: grid; grid-template-columns: repeat(4, 1fr); gap: .9rem;
  margin-bottom: 1.5rem;
}
.ers-stat {
  background: #F8FAFC; border: 1.5px solid #E2E8F0; border-radius: 16px;
  padding: 1rem .5rem; display: flex; flex-direction: column; align-items: center; gap: .3rem;
}
.ers-stat-icon { font-size: 1.3rem; }
.ers-stat-value { font-size: 1.15rem; font-weight: 800; color: #1A1A1A; }
.ers-stat-value small { font-size: .8rem; font-weight: 600; color: #9CA3AF; }
.ers-stat-label { font-size: .74rem; color: #6B7280; font-weight: 600; }

.ers-meta {
  position: relative; z-index: 1;
  font-size: .82rem; color: #9CA3AF; margin-bottom: 1rem;
  display: flex; justify-content: center; gap: .5rem; flex-wrap: wrap;
}
.ers-meta-dot { opacity: .6; }

.ers-release-note {
  position: relative; z-index: 1;
  background: rgba(245, 158, 11, .08); color: #b45309;
  border: 1.5px solid rgba(245, 158, 11, .2);
  border-radius: 14px; padding: .85rem 1.1rem;
  font-size: .88rem; font-weight: 700; margin-bottom: 1.5rem;
}

.ers-actions {
  position: relative; z-index: 1;
  display: flex; justify-content: center; gap: .85rem; flex-wrap: wrap;
}
.ers-btn {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .85rem 1.9rem; border-radius: 99px;
  font-family: 'Cairo', sans-serif; font-size: .95rem; font-weight: 700;
  text-decoration: none; cursor: pointer; border: none;
  transition: transform .25s, box-shadow .25s;
}
.ers-btn--primary { background: linear-gradient(135deg, #5B5FEF, #7C4DFF); color: #fff; box-shadow: 0 12px 30px rgba(91, 95, 239, .3); }
.ers-btn--primary:hover { transform: translateY(-3px); box-shadow: 0 20px 45px rgba(91, 95, 239, .45); color: #fff; }
.ers-btn--accent { background: linear-gradient(135deg, #00C2FF, #00D4A6); color: #fff; box-shadow: 0 12px 30px rgba(0, 212, 166, .25); }
.ers-btn--accent:hover { transform: translateY(-3px); box-shadow: 0 20px 45px rgba(0, 212, 166, .4); color: #fff; }
.ers-btn--muted { background: #F1F5F9; color: #475569; }
.ers-btn--muted:hover { background: #E2E8F0; color: #475569; }

@media (max-width: 640px) {
  .ers-card { padding: 1.75rem 1.25rem; border-radius: 20px; }
  .ers-stats { grid-template-columns: repeat(2, 1fr); }
  .ers-ring { width: 130px; height: 130px; }
  .ers-pct { font-size: 1.7rem; }
  .ers-actions { flex-direction: column; }
  .ers-btn { width: 100%; justify-content: center; }
}
</style>
